// Company Policies (phase 1, LD-POL-001..016) — the firm's system of record for policies, training,
// and acknowledgment. Five-layer spine + sub-tab lenses over one surface (LD-DESIGN-011/012 #1).
// All routes are namespaced /company-policies (insurance coverage owns /policies). Self-service ack
// writes go through the ACK_COMPANY_POLICY-gated open/viewed/acknowledge endpoints.
import React, { useState, useEffect, useCallback, useRef } from "react";
import { apiFetch, fmt, can, getPermissions } from "../api";
import {
  PageHeader, SummaryRibbon, RibbonCell, SubNav, SectionHeader, FilterBar, TableShell,
  StatusPill, EmptyState, Spinner, ErrorBanner, KeyValue,
} from "../components/ui";

const CAT_TONE = "slate";
const POLICY_STATUS_DISPLAY = { active: "Active", archived: "Archived" };
const VERSION_STATUS_DISPLAY = {
  draft: "Draft", scheduled: "Scheduled", active: "Published", superseded: "Superseded",
  retired: "Retired",
};
const versionTone = (s) => ({ active: "green", scheduled: "blue", draft: "slate",
  superseded: "slate", retired: "slate" }[s] || "slate");

function pct(done, total) { return total ? Math.round((100 * done) / total) : null; }

// ── small modal shell (the kit's modal-shell unification is still pending) ──
function Modal({ title, onClose, children, wide }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 1000,
                  display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 24,
                  overflowY: "auto" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           style={{ background: "#fff", borderRadius: 8, width: wide ? 820 : 520, maxWidth: "100%",
                    boxShadow: "var(--shadow2)", overflow: "hidden" }}>
        <div style={{ background: "linear-gradient(135deg,var(--navy),var(--navy2))", color: "#fff",
                      padding: "12px 18px", fontWeight: 600, display: "flex",
                      justifyContent: "space-between", alignItems: "center" }}>
          <span>{title}</span>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#fff",
                  fontSize: 20, cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: 18 }}>{children}</div>
      </div>
    </div>
  );
}

function AckBar({ done, total }) {
  const p = pct(done, total);
  const color = p == null ? "var(--border)" : p >= 100 ? "var(--green)" : p >= 60 ? "var(--yellow)" : "var(--red)";
  if (p == null) return <span style={{ color: "var(--text3)", fontSize: 11 }}>n/a — reference</span>;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 140 }}>
      <div style={{ flex: 1, height: 7, background: "var(--bg3)", borderRadius: 4, overflow: "hidden" }}>
        <div style={{ width: `${p}%`, height: "100%", background: color }} />
      </div>
      <span style={{ fontSize: 11, color: "var(--text2)", width: 32 }}>{p}%</span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
export default function Policies() {
  const [lens, setLens] = useState("library");
  const [ribbon, setRibbon] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const canManage = can("manage_company_policies");
  const canCompliance = can("view_policy_compliance");
  const canManageTraining = can("manage_training");
  // Access & Roles lens: HR/Exec/Ownership only. No "HR" role exists — HR = the Policy-Admin flag
  // (Alex/HR holds it), which is what manage_company_policies reflects; plus executive + ownership
  // (+ system_admin, via manage). Adjust here if the intended set changes.
  const _roles = getPermissions().roles || [];
  const canAccessLens = canManage || _roles.includes("executive") || _roles.includes("ownership");
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = useCallback(() => setRefreshKey((k) => k + 1), []);

  // ribbon vitals — derived from the library + my compliance
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [lib, mine] = await Promise.all([
          apiFetch("/company-policies?status=active"),
          apiFetch("/company-policies/my"),
        ]);
        let outstanding = 0, dueReview = 0;
        (lib.policies || []).forEach((p) => {
          if (p.next_review_date && new Date(p.next_review_date) <= new Date()) dueReview += 1;
        });
        let compliance = null;
        if (canCompliance) {
          try {
            const c = await apiFetch("/company-policies/compliance");
            let done = 0, tot = 0;
            (c.by_policy || []).forEach((r) => { done += Number(r.done || 0); tot += Number(r.total || 0); });
            outstanding = tot - done;
            compliance = pct(done, tot);
          } catch (_) { /* non-compliance viewer */ }
        }
        if (live) setRibbon({
          active: (lib.policies || []).filter((p) => p.status === "active").length,
          outstanding, dueReview, compliance,
          myOpen: mine.open_count || 0,
        });
      } catch (_) { /* ribbon is best-effort */ }
    })();
    return () => { live = false; };
  }, [refreshKey, canCompliance]);

  const lensOptions = [
    ["library", "Library"],
    ["training", "Assignments & Training"],
    ...(canCompliance ? [["registers", "Registers"]] : []),
    ["mine", "My Compliance"],
    ...(canAccessLens ? [["access", "Access & Roles"]] : []),
  ];

  return (
    <div className="page">
      <PageHeader
        title="Company Policies"
        subtitle="The firm's system of record for policies, procedures, training — versioned, acknowledged, and retained to HIPAA's 6-year rule."
        actions={canCompliance || canManage ? (
          <div style={{ display: "flex", gap: 8 }}>
            {canManage && <NewPolicyButton onCreated={bump} />}
          </div>
        ) : null}
      />

      <SummaryRibbon>
        <RibbonCell icon="📄" label="Active Policies" value={ribbon ? ribbon.active : "—"} />
        {canCompliance && <RibbonCell icon="✍️" label="Acks Outstanding"
          value={ribbon ? ribbon.outstanding : "—"} color={ribbon && ribbon.outstanding ? "var(--yellow)" : undefined} />}
        <RibbonCell icon="🎓" label="My Open Items" value={ribbon ? ribbon.myOpen : "—"}
          color={ribbon && ribbon.myOpen ? "var(--yellow)" : undefined} />
        <RibbonCell icon="🔄" label="Due For Review" value={ribbon ? ribbon.dueReview : "—"}
          color={ribbon && ribbon.dueReview ? "var(--red)" : undefined} />
        {canCompliance && <RibbonCell icon="✓" label="Compliance"
          value={ribbon && ribbon.compliance != null ? `${ribbon.compliance}%` : "—"}
          color={ribbon && ribbon.compliance >= 90 ? "var(--green)" : "var(--yellow)"} sub="firm-wide" />}
      </SummaryRibbon>

      <SubNav value={lens} onChange={(l) => { setLens(l); setDetailId(null); }} options={lensOptions} />

      {lens === "library" && (detailId
        ? <PolicyDetail policyId={detailId} onBack={() => setDetailId(null)} canManage={canManage}
                        canCompliance={canCompliance} onChanged={bump} />
        : <Library onOpen={setDetailId} canCompliance={canCompliance} refreshKey={refreshKey} />)}
      {lens === "training" && <Training canManage={canManageTraining} onChanged={bump} refreshKey={refreshKey} />}
      {lens === "registers" && canCompliance && <Registers refreshKey={refreshKey} />}
      {lens === "mine" && <MyCompliance onChanged={bump} refreshKey={refreshKey} />}
      {lens === "access" && canAccessLens && <AccessRoles />}
    </div>
  );
}

// ── Library lens ────────────────────────────────────────────────────────────
function Library({ onOpen, canCompliance, refreshKey }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    let live = true;
    setData(null); setErr(null);
    apiFetch("/company-policies").then((d) => { if (live) setData(d); })
      .catch((e) => { if (live) setErr(String(e.message || e)); });
    return () => { live = false; };
  }, [refreshKey]);

  if (err) return <ErrorBanner>{err}</ErrorBanner>;
  if (!data) return <Spinner label="Loading policies…" />;

  const cats = {};
  data.policies.forEach((p) => { if (p.category) cats[p.category] = (cats[p.category] || 0) + 1; });
  const catList = Object.keys(cats).sort();
  let rows = data.policies;
  if (cat !== "all") rows = rows.filter((p) => p.category === cat);
  if (q) rows = rows.filter((p) => p.title.toLowerCase().includes(q.toLowerCase()));

  return (
    <>
      <FilterBar onReset={cat !== "all" || q ? () => { setCat("all"); setQ(""); } : null}>
        <input className="input" placeholder="Search policies, procedures, trainings…"
               value={q} onChange={(e) => setQ(e.target.value)} style={{ flex: 1, minWidth: 200 }} />
        <Pill active={cat === "all"} onClick={() => setCat("all")} label="All" count={data.policies.length} />
        {catList.map((c) => <Pill key={c} active={cat === c} onClick={() => setCat(c)} label={c} count={cats[c]} />)}
      </FilterBar>

      {rows.length === 0
        ? <EmptyState icon="📄" title="No policies" hint="Publish a policy to start the library." />
        : (
        <TableShell sticky>
          <thead><tr>
            <th>Policy / Document</th><th>Category</th><th>Owner</th><th>Version</th>
            <th>Effective</th><th>Next Review</th><th>Audience</th>
            {canCompliance && <th>Acknowledged</th>}<th>Status</th>
          </tr></thead>
          <tbody>
            {rows.map((p) => {
              const reviewDue = p.next_review_date && new Date(p.next_review_date) <= new Date();
              return (
                <tr key={p.id} style={{ cursor: "pointer" }} onClick={() => onOpen(p.id)}>
                  <td style={{ fontWeight: 600 }}>{p.title}</td>
                  <td>{p.category || "—"}</td>
                  <td>{p.owner_name || "—"}</td>
                  <td className="mono">{p.cur_version_label || "—"}</td>
                  <td className="mono">{fmt.date(p.cur_effective_date)}</td>
                  <td className="mono" style={reviewDue ? { color: "var(--red)" } : undefined}>
                    {p.next_review_date ? fmt.date(p.next_review_date) : "—"}</td>
                  <td>{!p.requires_ack ? "Reference" : "—"}</td>
                  {canCompliance && <td>{p.requires_ack
                    ? <AckBar done={p.ack_done || 0} total={p.ack_total || 0} />
                    : <span style={{ color: "var(--text3)", fontSize: 11 }}>n/a — reference</span>}</td>}
                  <td>{reviewDue
                    ? <StatusPill tone="red" sm>Review due</StatusPill>
                    : <StatusPill tone={p.status === "archived" ? "slate" : "green"} sm>
                        {POLICY_STATUS_DISPLAY[p.status] || p.status}</StatusPill>}</td>
                </tr>
              );
            })}
          </tbody>
        </TableShell>
      )}
    </>
  );
}

function Pill({ active, onClick, label, count }) {
  return (
    <button onClick={onClick} className={`btn btn-sm ${active ? "btn-navy" : "btn-outline"}`}
            style={{ borderRadius: 16 }}>
      {label}{count != null && <span style={{ opacity: 0.7, marginLeft: 5 }}>{count}</span>}
    </button>
  );
}

// ── Policy detail ────────────────────────────────────────────────────────────
function PolicyDetail({ policyId, onBack, canManage, canCompliance, onChanged }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [showPublish, setShowPublish] = useState(false);
  const [showAudience, setShowAudience] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [busyArchive, setBusyArchive] = useState(false);
  const [busyAssign, setBusyAssign] = useState(false);
  const [busyDelete, setBusyDelete] = useState(false);

  const load = useCallback(() => {
    setData(null);
    apiFetch(`/company-policies/${policyId}`).then(setData).catch((e) => setErr(String(e.message || e)));
  }, [policyId]);
  useEffect(load, [load]);

  const archive = async () => {
    if (!window.confirm("Retire this policy? It stays intact — every version and acknowledgment is "
      + "retained — but it leaves the active library. You can't undo this from the UI.")) return;
    setBusyArchive(true);
    try {
      await apiFetch(`/company-policies/${policyId}/archive`, { method: "POST" });
      onChanged();
      onBack();
    } catch (e) { window.alert(String(e.message || e)); setBusyArchive(false); }
  };

  const assignAcks = async () => {
    if (!window.confirm("Require acknowledgment now? Everyone in the current audience will be assigned "
      + "to read & sign the current version.")) return;
    setBusyAssign(true);
    try {
      const r = await apiFetch(`/company-policies/${policyId}/assign-acks`, { method: "POST" });
      window.alert(`Assigned to ${r.assigned} staff (audience of ${r.audience}).`);
      onChanged(); load();
    } catch (e) { window.alert(String(e.message || e)); }
    finally { setBusyAssign(false); }
  };

  const deletePolicy = async () => {
    const inForce = (data.versions || []).some((v) => ["active", "superseded", "retired"].includes(v.status));
    const msg = inForce
      ? "PERMANENTLY DELETE this policy and ALL its versions, documents and acknowledgments?\n\nIt has been in force — HIPAA retention normally means you RETIRE instead. This is a system-admin break-glass action, it is audit-logged, and it cannot be undone from the UI."
      : "Delete this draft policy and its file? It was never in force. This cannot be undone from the UI.";
    if (!window.confirm(msg)) return;
    setBusyDelete(true);
    try {
      await apiFetch(`/company-policies/${policyId}`, { method: "DELETE" });
      onChanged();
      onBack();
    } catch (e) { window.alert(String(e.message || e)); setBusyDelete(false); }
  };

  if (err) return <ErrorBanner>{err}</ErrorBanner>;
  if (!data) return <Spinner label="Loading policy…" />;
  const { policy, versions, ack_roster } = data;
  const active = versions.find((v) => v.status === "active");

  return (
    <>
      <button className="btn btn-outline btn-sm" onClick={onBack} style={{ marginBottom: 12 }}>← Back to Library</button>

      <div style={{ background: "linear-gradient(135deg,var(--navy),var(--navy2))", color: "#fff",
                    borderRadius: "8px 8px 0 0", padding: "12px 18px", display: "flex",
                    justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <span style={{ fontWeight: 600, fontSize: 15 }}>📘 {policy.title}</span>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <StatusPill tone={policy.status === "archived" ? "slate" : "green"} sm>
            {POLICY_STATUS_DISPLAY[policy.status] || policy.status}</StatusPill>
          {canManage && <button className="btn btn-outline btn-sm" onClick={() => setShowEdit(true)}
                                style={{ color: "#fff", borderColor: "rgba(255,255,255,0.4)" }}>Edit</button>}
          {canManage && <button className="btn btn-outline btn-sm" onClick={() => setShowAudience(true)}
                                style={{ color: "#fff", borderColor: "rgba(255,255,255,0.4)" }}>Audience</button>}
          {canManage && active && policy.requires_ack && policy.status !== "archived" && (
            <button className="btn btn-outline btn-sm" disabled={busyAssign} onClick={assignAcks}
                    style={{ color: "#fff", borderColor: "rgba(255,255,255,0.4)" }}>
              {busyAssign ? "Assigning…" : "Require ack now"}</button>)}
          {canManage && policy.status !== "archived" && (
            <button className="btn btn-outline btn-sm" disabled={busyArchive} onClick={archive}
                    style={{ color: "#fff", borderColor: "rgba(255,255,255,0.4)" }}>
              {busyArchive ? "Retiring…" : "Retire"}</button>)}
          {canManage && (
            <button className="btn btn-outline btn-sm" disabled={busyDelete} onClick={deletePolicy}
                    style={{ color: "#fff", borderColor: "rgba(255,150,150,0.7)" }}>
              {busyDelete ? "Deleting…" : "Delete"}</button>)}
          {canManage && <button className="btn btn-blue btn-sm" onClick={() => setShowPublish(true)}>+ Publish new version</button>}
        </div>
      </div>
      <div style={{ border: "1px solid var(--border)", borderTop: "none", borderRadius: "0 0 8px 8px",
                    padding: 18, marginBottom: 16, background: "#fff", display: "flex", gap: 24,
                    flexWrap: "wrap", alignItems: "stretch" }}>
        <div style={{ flex: "0 0 300px", minWidth: 240, maxWidth: "100%", display: "grid",
                      gridTemplateColumns: "1fr", gap: 2, alignContent: "start" }}>
          <KeyValue label="Category">{policy.category || "—"}</KeyValue>
          <KeyValue label="Document type">{policy.document_type}</KeyValue>
          <KeyValue label="Owner">{policy.owner_name || "—"}</KeyValue>
          <KeyValue label="Current version"><span className="mono">{active ? active.version_label : "—"}</span></KeyValue>
          <KeyValue label="Effective"><span className="mono">{active ? fmt.date(active.effective_date) : "—"}</span></KeyValue>
          <KeyValue label="Requires acknowledgment">{policy.requires_ack ? "Yes — audience re-signs each major revision" : "No — reference only"}</KeyValue>
          <KeyValue label="Next review"><span className="mono">{policy.next_review_date ? fmt.date(policy.next_review_date) : "—"}</span></KeyValue>
          <KeyValue label="Retention">6 yr after superseded{active && active.legal_hold ? " · legal hold" : ""}</KeyValue>
        </div>
        <div style={{ flex: "1 1 460px", minWidth: 280 }}>
          {active
            ? <FileViewer versionId={active.id} label={`${policy.title} ${active.version_label}`} height={560} fill />
            : <EmptyState icon="📄" title="No published version" hint="Publish a version to attach the document." />}
        </div>
      </div>

      {/* 2b: version history + ack roster are a compliance-only view */}
      {!data.history_restricted && (
      <div style={{ display: "grid", gridTemplateColumns: ack_roster ? "1.4fr 1fr" : "1fr", gap: 16 }}>
        <div>
          <SectionHeader title="Version history" subtitle="immutable · retained" />
          <TableShell>
            <thead><tr><th>Ver</th><th>Effective</th><th>Published by</th><th>Change summary</th><th>Acks</th><th>Status</th></tr></thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.id}>
                  <td className="mono" style={{ fontWeight: 600 }}>{v.version_label}
                    {v.status === "active" && <StatusPill tone="green" sm style={{ marginLeft: 6 }}>current</StatusPill>}</td>
                  <td className="mono">{fmt.date(v.effective_date)}</td>
                  <td>{v.published_by_name || "—"}</td>
                  <td>{v.change_summary || "—"}</td>
                  <td className="mono">{v.ack_total ? `${v.ack_done}/${v.ack_total}` : "—"}</td>
                  <td><StatusPill tone={versionTone(v.status)} sm>{VERSION_STATUS_DISPLAY[v.status] || v.status}</StatusPill></td>
                </tr>
              ))}
            </tbody>
          </TableShell>
          <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 8 }}>
            A superseded version is never edited or deleted — it is retained with its own ack roster, so
            "what did the policy say on any past date, and who was on notice" is always answerable.
          </div>
        </div>
        {ack_roster && (
          <div>
            <SectionHeader title={`Acknowledgment${active ? " — " + active.version_label : ""}`} />
            <TableShell>
              <thead><tr><th>Staff</th><th>Status</th><th>When</th></tr></thead>
              <tbody>
                {ack_roster.length === 0 && <tr><td colSpan={3} style={{ color: "var(--text3)" }}>No audience assigned.</td></tr>}
                {ack_roster.map((r) => {
                  const overdue = !r.acknowledged_at && r.due_at && new Date(r.due_at) < new Date();
                  return (
                    <tr key={r.user_id}>
                      <td>{r.full_name}</td>
                      <td><StatusPill tone={r.acknowledged_at ? "green" : overdue ? "red" : "amber"} sm>
                        {r.acknowledged_at ? "Signed" : overdue ? "Overdue" : "Pending"}</StatusPill></td>
                      <td className="mono" style={{ fontSize: 11 }}>
                        {r.acknowledged_at ? fmt.date(r.acknowledged_at)
                          : r.due_at ? `due ${fmt.date(r.due_at)}` : "—"}
                        {r.method === "paper" && " (paper)"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </TableShell>
          </div>
        )}
      </div>
      )}

      {showPublish && <PublishModal policyId={policyId} onClose={() => setShowPublish(false)}
        onDone={() => { setShowPublish(false); load(); onChanged(); }} />}
      {showAudience && <AudienceModal policyId={policyId} onClose={() => setShowAudience(false)}
        onDone={() => { setShowAudience(false); load(); }} />}
      {showEdit && <EditPolicyModal policy={policy} onClose={() => setShowEdit(false)}
        onDone={() => { setShowEdit(false); load(); onChanged(); }} />}
    </>
  );
}

// ── PDF viewer (used in detail + read&sign) ─────────────────────────────────
function FileViewer({ versionId, label, onViewed, height = 300, fill }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let obj;
    let live = true;
    apiFetch(`/company-policies/versions/${versionId}/file`, { raw: true })
      .then((r) => r.blob()).then((b) => { obj = URL.createObjectURL(b); if (live) setUrl(obj); })
      .catch((e) => { if (live) setErr(String(e.message || e)); });
    return () => { live = false; if (obj) URL.revokeObjectURL(obj); };
  }, [versionId]);
  if (err) return <ErrorBanner>{err}</ErrorBanner>;
  if (!url) return <Spinner label="Loading document…" />;
  return (
    <div style={fill ? { display: "flex", flexDirection: "column", height: "100%" } : undefined}>
      <embed src={url} type="application/pdf" style={{ width: "100%", height: fill ? "100%" : height,
             minHeight: fill ? height : undefined, flex: fill ? 1 : undefined,
             border: "1px solid var(--border)", borderRadius: 6, background: "var(--bg3)" }} />
      <div style={{ marginTop: 6, display: "flex", gap: 10, alignItems: "center" }}>
        <a href={url} target="_blank" rel="noreferrer" className="btn btn-outline btn-sm">Open document ↗</a>
        {onViewed && <span style={{ fontSize: 11, color: "var(--text3)" }}>{label}</span>}
      </div>
    </div>
  );
}

// ── New policy button + modal ───────────────────────────────────────────────
function NewPolicyButton({ onCreated }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="btn btn-blue" onClick={() => setOpen(true)}>+ New policy</button>
      {open && <NewPolicyModal onClose={() => setOpen(false)} onDone={() => { setOpen(false); onCreated(); }} />}
    </>
  );
}

function NewPolicyModal({ onClose, onDone }) {
  const [f, setF] = useState({ title: "", document_type: "policy", category: "", requires_ack: true,
    review_cadence_months: "" });
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => { apiFetch("/custom-lists/policy_categories/items").then((d) =>
    setCats((d.items || d || []).map((i) => i.value || i))).catch(() => setCats([])); }, []);
  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const body = { ...f, review_cadence_months: f.review_cadence_months ? Number(f.review_cadence_months) : null };
      await apiFetch("/company-policies", { method: "POST", body });
      onDone();
    } catch (e) { setErr(String(e.message || e)); setBusy(false); }
  };
  return (
    <Modal title="New policy" onClose={onClose}>
      {err && <ErrorBanner>{err}</ErrorBanner>}
      <Field label="Title"><input className="input" value={f.title}
        onChange={(e) => setF({ ...f, title: e.target.value })} autoFocus /></Field>
      <Field label="Document type">
        <select className="input" value={f.document_type} onChange={(e) => setF({ ...f, document_type: e.target.value })}>
          <option value="policy">Policy</option><option value="procedure">Procedure</option>
          <option value="form">Form</option><option value="reference">Reference</option>
        </select></Field>
      <Field label="Category">
        <input className="input" list="cp-cats" value={f.category}
          onChange={(e) => setF({ ...f, category: e.target.value })} />
        <datalist id="cp-cats">{cats.map((c) => <option key={c} value={c} />)}</datalist></Field>
      <Field label="Review cadence (months)"><input className="input" type="number" value={f.review_cadence_months}
        onChange={(e) => setF({ ...f, review_cadence_months: e.target.value })} placeholder="e.g. 12 (blank = none)" /></Field>
      <label style={{ display: "flex", gap: 8, alignItems: "center", margin: "10px 0", fontSize: 13 }}>
        <input type="checkbox" checked={f.requires_ack} onChange={(e) => setF({ ...f, requires_ack: e.target.checked })} />
        Requires acknowledgment (PDF, audience re-signs each major revision)
      </label>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
        <button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn btn-blue" disabled={busy || !f.title} onClick={save}>{busy ? "Saving…" : "Create"}</button>
      </div>
    </Modal>
  );
}

// ── Edit policy metadata modal (PATCH) ─────────────────────────────────────
function EditPolicyModal({ policy, onClose, onDone }) {
  const [f, setF] = useState({
    title: policy.title || "",
    document_type: policy.document_type || "policy",
    category: policy.category || "",
    requires_ack: !!policy.requires_ack,
    review_cadence_months: policy.review_cadence_months == null ? "" : String(policy.review_cadence_months),
    next_review_date: policy.next_review_date ? String(policy.next_review_date).slice(0, 10) : "",
  });
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => { apiFetch("/custom-lists/policy_categories/items").then((d) =>
    setCats((d.items || d || []).map((i) => i.value || i))).catch(() => setCats([])); }, []);
  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const body = {
        title: f.title,
        document_type: f.document_type,
        category: f.category || null,
        requires_ack: f.requires_ack,
        review_cadence_months: f.review_cadence_months ? Number(f.review_cadence_months) : null,
        next_review_date: f.next_review_date || null,
      };
      await apiFetch(`/company-policies/${policy.id}`, { method: "PATCH", body });
      onDone();
    } catch (e) { setErr(String(e.message || e)); setBusy(false); }
  };
  return (
    <Modal title="Edit policy" onClose={onClose}>
      {err && <ErrorBanner>{err}</ErrorBanner>}
      <Field label="Title"><input className="input" value={f.title}
        onChange={(e) => setF({ ...f, title: e.target.value })} autoFocus /></Field>
      <Field label="Document type">
        <select className="input" value={f.document_type} onChange={(e) => setF({ ...f, document_type: e.target.value })}>
          <option value="policy">Policy</option><option value="procedure">Procedure</option>
          <option value="form">Form</option><option value="reference">Reference</option>
        </select></Field>
      <Field label="Category">
        <input className="input" list="cp-cats-edit" value={f.category}
          onChange={(e) => setF({ ...f, category: e.target.value })} />
        <datalist id="cp-cats-edit">{cats.map((c) => <option key={c} value={c} />)}</datalist></Field>
      <Field label="Review cadence (months)"><input className="input" type="number" value={f.review_cadence_months}
        onChange={(e) => setF({ ...f, review_cadence_months: e.target.value })} placeholder="blank = none" /></Field>
      <Field label="Next review date"><input className="input" type="date" value={f.next_review_date}
        onChange={(e) => setF({ ...f, next_review_date: e.target.value })} /></Field>
      <label style={{ display: "flex", gap: 8, alignItems: "center", margin: "10px 0", fontSize: 13 }}>
        <input type="checkbox" checked={f.requires_ack} onChange={(e) => setF({ ...f, requires_ack: e.target.checked })} />
        Requires acknowledgment
      </label>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
        <button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn btn-blue" disabled={busy || !f.title} onClick={save}>{busy ? "Saving…" : "Save changes"}</button>
      </div>
    </Modal>
  );
}

// ── Publish new version modal (upload draft + publish) ──────────────────────
function PublishModal({ policyId, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [f, setF] = useState({ version_label: "", version_kind: "major", effective_date: "",
    change_summary: "", ack_window_days: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const publish = async () => {
    if (!file) { setErr("Choose a PDF file."); return; }
    setBusy(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("version_label", f.version_label);
      const ver = await (await apiFetch(`/company-policies/${policyId}/versions`,
        { method: "POST", raw: true, body: fd })).json();
      const body = { version_kind: f.version_kind, change_summary: f.change_summary };
      if (f.effective_date) body.effective_date = f.effective_date;
      if (f.ack_window_days) body.ack_window_days = Number(f.ack_window_days);
      await apiFetch(`/company-policies/versions/${ver.id}/publish`, { method: "POST", body });
      onDone();
    } catch (e) { setErr(String(e.message || e)); setBusy(false); }
  };
  return (
    <Modal title="Publish new version" onClose={onClose}>
      {err && <ErrorBanner>{err}</ErrorBanner>}
      <Field label="Version label"><input className="input" placeholder="v2.0" value={f.version_label}
        onChange={(e) => setF({ ...f, version_label: e.target.value })} /></Field>
      <Field label="Document (PDF)"><input type="file" accept="application/pdf"
        onChange={(e) => setFile(e.target.files[0])} /></Field>
      <Field label="Change type">
        <select className="input" value={f.version_kind} onChange={(e) => setF({ ...f, version_kind: e.target.value })}>
          <option value="major">Major — everyone re-signs (default, safe choice)</option>
          <option value="minor">Minor — prior signatures carry forward</option>
        </select></Field>
      <Field label="Effective date"><input className="input" type="date" value={f.effective_date}
        onChange={(e) => setF({ ...f, effective_date: e.target.value })} />
        <span style={{ fontSize: 11, color: "var(--text3)" }}> blank = today; a future date schedules it</span></Field>
      <Field label="Change summary (required)"><textarea className="input" rows={2} value={f.change_summary}
        onChange={(e) => setF({ ...f, change_summary: e.target.value })} /></Field>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
        <button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn btn-blue" disabled={busy || !file || !f.version_label || !f.change_summary.trim()}
          onClick={publish}>{busy ? "Publishing…" : "Publish"}</button>
      </div>
    </Modal>
  );
}

// ── Audience editor ─────────────────────────────────────────────────────────
const ROLE_OPTS = [
  ["adjuster", "Adjusters"], ["claims_support", "Claims Support"], ["accountant", "Accountants"],
  ["claims_manager", "Managers"], ["executive", "Executives"], ["ownership", "Ownership"],
  ["system_admin", "System Admin"],
];
function AudienceModal({ policyId, onClose, onDone }) {
  const [targets, setTargets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => { apiFetch(`/company-policies/${policyId}/audience`).then((d) =>
    setTargets(d.targets || [])).catch((e) => setErr(String(e.message || e))); }, [policyId]);
  const toggleAll = () => {
    setTargets((t) => t.some((x) => x.target_kind === "all")
      ? t.filter((x) => x.target_kind !== "all")
      : [...t.filter((x) => x.target_kind !== "all"), { target_kind: "all", mode: "include" }]);
  };
  const toggleRole = (rc) => {
    setTargets((t) => t.some((x) => x.target_kind === "role" && x.target_ref === rc)
      ? t.filter((x) => !(x.target_kind === "role" && x.target_ref === rc))
      : [...t, { target_kind: "role", target_ref: rc, mode: "include" }]);
  };
  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await apiFetch(`/company-policies/${policyId}/audience`,
        { method: "PUT", body: { targets: targets.map((t) => ({ target_kind: t.target_kind,
          target_ref: t.target_ref || null, mode: t.mode || "include" })) } });
      onDone();
    } catch (e) { setErr(String(e.message || e)); setBusy(false); }
  };
  const hasAll = targets.some((x) => x.target_kind === "all");
  return (
    <Modal title="Acknowledgment audience" onClose={onClose}>
      {err && <ErrorBanner>{err}</ErrorBanner>}
      <p style={{ fontSize: 12, color: "var(--text2)" }}>Who must acknowledge this policy. A major
        publish assigns the ack to everyone in the resolved audience.</p>
      <label style={{ display: "flex", gap: 8, alignItems: "center", margin: "8px 0", fontWeight: 600 }}>
        <input type="checkbox" checked={hasAll} onChange={toggleAll} /> All staff
      </label>
      {!hasAll && ROLE_OPTS.map(([rc, label]) => (
        <label key={rc} style={{ display: "flex", gap: 8, alignItems: "center", margin: "5px 0", fontSize: 13 }}>
          <input type="checkbox" checked={targets.some((x) => x.target_kind === "role" && x.target_ref === rc)}
            onChange={() => toggleRole(rc)} /> {label}
        </label>
      ))}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
        <button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn btn-blue" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save audience"}</button>
      </div>
    </Modal>
  );
}

// ── My Compliance lens ──────────────────────────────────────────────────────
function MyCompliance({ refreshKey }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [signVid, setSignVid] = useState(null);
  const load = useCallback(() => {
    setData(null);
    apiFetch("/company-policies/my").then(setData).catch((e) => setErr(String(e.message || e)));
  }, []);
  useEffect(load, [load, refreshKey]);
  if (err) return <ErrorBanner>{err}</ErrorBanner>;
  if (!data) return <Spinner label="Loading your compliance…" />;

  return (
    <>
      <div style={{ background: "linear-gradient(135deg,var(--navy),var(--navy2))", color: "#fff",
                    borderRadius: "8px 8px 0 0", padding: "10px 16px", fontWeight: 600,
                    display: "flex", justifyContent: "space-between" }}>
        <span>👤 What I need to do</span>
        <span style={{ opacity: 0.85, fontWeight: 500 }}>{data.open_count} open item{data.open_count === 1 ? "" : "s"}</span>
      </div>
      <div style={{ border: "1px solid var(--border)", borderTop: "none", borderRadius: "0 0 8px 8px",
                    marginBottom: 18, background: "#fff", overflow: "hidden" }}>
        {data.open_acks.length === 0 && data.due_training.length === 0
          ? <div style={{ padding: 20 }}><EmptyState icon="✅" title="You're all caught up"
              hint="No policies to sign and no training due." /></div>
          : (
          <table style={{ width: "100%" }}><tbody>
            {data.open_acks.map((a) => (
              <tr key={"a" + a.policy_version_id}>
                <td style={{ padding: "10px 16px", fontWeight: 600 }}>{a.title}
                  <div style={{ fontSize: 11, color: "var(--text3)", fontWeight: 400 }}>Acknowledgment · {a.version_label}</div></td>
                <td className="mono">{a.due_at ? `due ${fmt.date(a.due_at)}` : "—"}</td>
                <td><StatusPill tone="amber" sm>Awaiting your signature</StatusPill></td>
                <td style={{ textAlign: "right", paddingRight: 16 }}>
                  <button className="btn btn-blue btn-sm" onClick={() => setSignVid(a.policy_version_id)}>Read &amp; sign</button></td>
              </tr>
            ))}
            {data.due_training.map((t) => (
              <tr key={"t" + t.id}>
                <td style={{ padding: "10px 16px", fontWeight: 600 }}>{t.name}
                  <div style={{ fontSize: 11, color: "var(--text3)", fontWeight: 400 }}>Training</div></td>
                <td className="mono">{t.due_at ? `due ${fmt.date(t.due_at)}` : "—"}</td>
                <td><StatusPill tone="blue" sm>Not started</StatusPill></td>
                <td style={{ textAlign: "right", paddingRight: 16 }}>
                  <TrainingCompleteButton courseId={t.course_id} onDone={load} /></td>
              </tr>
            ))}
          </tbody></table>
        )}
      </div>

      <SectionHeader title="Policies available to me" />
      <TableShell sticky>
        <thead><tr><th>Policy</th><th>Category</th><th>Version</th><th>My acknowledgment</th></tr></thead>
        <tbody>
          {data.available.map((p) => (
            <tr key={p.id}>
              <td style={{ fontWeight: 600 }}>{p.title}</td>
              <td>{p.category || "—"}</td>
              <td className="mono">{p.version_label || "—"}</td>
              <td>{!p.requires_ack
                ? <StatusPill tone="slate" sm>Reference only</StatusPill>
                : p.acknowledged_at
                  ? <StatusPill tone="green" sm>Signed {fmt.date(p.acknowledged_at)}</StatusPill>
                  : <StatusPill tone="amber" sm>Not signed</StatusPill>}</td>
            </tr>
          ))}
        </tbody>
      </TableShell>

      {signVid && <ReadAndSignModal versionId={signVid} onClose={() => setSignVid(null)}
        onDone={() => { setSignVid(null); load(); }} />}
    </>
  );
}

// ── Read & sign (the defensible core, LD-POL-009) ───────────────────────────
function ReadAndSignModal({ versionId, onClose, onDone }) {
  const [reachedEnd, setReachedEnd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const openedRef = useRef(false);

  useEffect(() => {
    if (openedRef.current) return;
    openedRef.current = true;
    apiFetch(`/company-policies/versions/${versionId}/open`, { method: "POST" }).catch(() => {});
  }, [versionId]);

  const confirmRead = async () => {
    try { await apiFetch(`/company-policies/versions/${versionId}/viewed`, { method: "POST" }); setReachedEnd(true); }
    catch (e) { setErr(String(e.message || e)); }
  };
  const sign = async () => {
    setBusy(true); setErr(null);
    try {
      await apiFetch(`/company-policies/versions/${versionId}/acknowledge`,
        { method: "POST", body: { attestation_text: "I have read and understood this policy and agree to comply." } });
      onDone();
    } catch (e) { setErr(String(e.message || e)); setBusy(false); }
  };
  return (
    <Modal title="Read & sign" onClose={onClose} wide>
      {err && <ErrorBanner>{err}</ErrorBanner>}
      <FileViewer versionId={versionId} label="" height={520} onViewed />
      <div style={{ marginTop: 12, padding: 12, background: "var(--bg3)", borderRadius: 6 }}>
        {!reachedEnd
          ? <button className="btn btn-outline" onClick={confirmRead}>I've read to the end</button>
          : <>
              <p style={{ fontSize: 12.5, margin: "0 0 10px" }}>
                <strong>I have read and understood this policy and agree to comply.</strong> Your
                acknowledgment is recorded against this exact version with a timestamp.</p>
              <button className="btn btn-blue" disabled={busy} onClick={sign}>{busy ? "Signing…" : "Sign & acknowledge"}</button>
            </>}
      </div>
    </Modal>
  );
}

// ── Assignments & Training lens ─────────────────────────────────────────────
function Training({ canManage, refreshKey }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const load = useCallback(() => {
    setData(null);
    apiFetch("/company-policies/training/courses").then(setData).catch((e) => setErr(String(e.message || e)));
  }, []);
  useEffect(load, [load, refreshKey]);
  if (err) return <ErrorBanner>{err}</ErrorBanner>;
  if (!data) return <Spinner label="Loading training…" />;
  return (
    <>
      <SectionHeader title="Training courses" subtitle="Track-only: assignment, cadence, completion."
        actions={canManage ? <button className="btn btn-blue btn-sm" onClick={() => setShowNew(true)}>+ New course</button> : null} />
      {data.courses.length === 0
        ? <EmptyState icon="🎓" title="No courses" hint="Add a training course to track completions." />
        : (
        <TableShell sticky>
          <thead><tr><th>Course</th><th>Linked policy</th><th>Cadence</th><th>Delivery</th><th>Completed</th></tr></thead>
          <tbody>
            {data.courses.map((c) => (
              <tr key={c.id}>
                <td style={{ fontWeight: 600 }}>{c.name}</td>
                <td>{c.linked_policy_id ? `#${c.linked_policy_id}` : "—"}</td>
                <td>{c.cadence_months ? `${c.cadence_months} mo` : "—"}</td>
                <td>{c.delivery}</td>
                <td className="mono">{c.completed}/{c.assigned}</td>
              </tr>
            ))}
          </tbody>
        </TableShell>
      )}
      {showNew && <NewCourseModal onClose={() => setShowNew(false)} onDone={() => { setShowNew(false); load(); }} />}
    </>
  );
}

function NewCourseModal({ onClose, onDone }) {
  const [f, setF] = useState({ name: "", description: "", cadence_months: "", delivery: "external" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await apiFetch("/company-policies/training/courses", { method: "POST",
        body: { ...f, cadence_months: f.cadence_months ? Number(f.cadence_months) : null } });
      onDone();
    } catch (e) { setErr(String(e.message || e)); setBusy(false); }
  };
  return (
    <Modal title="New training course" onClose={onClose}>
      {err && <ErrorBanner>{err}</ErrorBanner>}
      <Field label="Name"><input className="input" value={f.name} autoFocus
        onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
      <Field label="Description"><textarea className="input" rows={2} value={f.description}
        onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
      <Field label="Cadence (months)"><input className="input" type="number" value={f.cadence_months}
        onChange={(e) => setF({ ...f, cadence_months: e.target.value })} /></Field>
      <Field label="Delivery">
        <select className="input" value={f.delivery} onChange={(e) => setF({ ...f, delivery: e.target.value })}>
          <option value="external">External</option><option value="upload">Certificate upload</option>
          <option value="in_app">In-app</option>
        </select></Field>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
        <button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn btn-blue" disabled={busy || !f.name} onClick={save}>{busy ? "Saving…" : "Create"}</button>
      </div>
    </Modal>
  );
}

function TrainingCompleteButton({ courseId, onDone }) {
  const [busy, setBusy] = useState(false);
  const done = async () => {
    setBusy(true);
    try { await apiFetch(`/company-policies/training/courses/${courseId}/complete`,
      { method: "POST", body: { method: "attestation" } }); onDone(); }
    catch (_) { setBusy(false); }
  };
  return <button className="btn btn-blue btn-sm" disabled={busy} onClick={done}>{busy ? "…" : "Mark complete"}</button>;
}

// ── Registers lens (policy update log + training log, derived) ───────────────
function Registers({ refreshKey }) {
  const [reg, setReg] = useState("policy");
  const [rows, setRows] = useState(null);
  useEffect(() => {
    setRows(null);
    apiFetch("/company-policies").then(async (d) => {
      const detail = await Promise.all(d.policies.slice(0, 60).map((p) =>
        apiFetch(`/company-policies/${p.id}`).catch(() => null)));
      const events = [];
      detail.forEach((dt) => { if (!dt) return;
        dt.versions.forEach((v) => { if (v.published_at) events.push({
          when: v.published_at, policy: dt.policy.title, version: v.version_label,
          summary: v.change_summary, by: v.published_by_name }); }); });
      events.sort((a, b) => new Date(b.when) - new Date(a.when));
      setRows(events);
    }).catch(() => setRows([]));
  }, [refreshKey]);
  return (
    <>
      <SubNav value={reg} onChange={setReg} options={[["policy", "Policy update log"], ["training", "Training log"]]} />
      {reg === "training"
        ? <EmptyState icon="🎓" title="Training log" hint="Completions appear here as they are recorded." />
        : rows == null ? <Spinner /> : rows.length === 0
          ? <EmptyState icon="📘" title="No published versions yet" />
          : (
          <TableShell sticky>
            <thead><tr><th>When</th><th>Policy</th><th>Version</th><th>Change</th><th>By</th></tr></thead>
            <tbody>
              {rows.map((e, i) => (
                <tr key={i}>
                  <td className="mono">{fmt.date(e.when)}</td>
                  <td style={{ fontWeight: 600 }}>{e.policy}</td>
                  <td className="mono">{e.version}</td>
                  <td>{e.summary || "—"}</td>
                  <td>{e.by || "—"}</td>
                </tr>
              ))}
            </tbody>
          </TableShell>
        )}
    </>
  );
}

// ── Access & Roles lens (informational matrix) ──────────────────────────────
function AccessRoles() {
  const MATRIX = [
    ["View library + acknowledge", "All internal staff"],
    ["See others' compliance status", "Managers · Executives · Ownership · System Admin"],
    ["Publish versions · manage audiences · training", "Ownership · System Admin · anyone with the Policy Administrator flag"],
    ["Break-glass delete of a published version", "System Admin only (blocked under legal hold)"],
  ];
  return (
    <>
      <SectionHeader title="Access & roles" subtitle="Who can do what in Company Policies." />
      <TableShell>
        <thead><tr><th>Capability</th><th>Held by</th></tr></thead>
        <tbody>{MATRIX.map(([c, w], i) => (
          <tr key={i}><td style={{ fontWeight: 600 }}>{c}</td><td>{w}</td></tr>))}</tbody>
      </TableShell>
      <div style={{ fontSize: 11.5, color: "var(--text3)", marginTop: 10 }}>
        The <strong>Policy Administrator</strong> flag is set per user in Admin → Users; it grants
        publish/manage rights regardless of role (holders at launch: Kuhrt, Alex, Krise). TPA partner
        roles and external client viewers have no access to this section.
      </div>
    </>
  );
}

// ── tiny field wrapper ──────────────────────────────────────────────────────
function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text2)", marginBottom: 3,
                    textTransform: "uppercase", letterSpacing: 0.3 }}>{label}</div>
      {children}
    </div>
  );
}
